<template>
  <main>
    <h1>Refunds Admin</h1>
    <form v-on:reset="handleReset">
      <!-- returning false to prevent page reload -->
      <!--SEARCH AND FILTER AREA-->
      <fieldset class="references">
        <legend>References</legend>
        <p>
          <label>Payout reference<input
              placeholder="ex: 123-4567890-PYT"
              v-model="refundRefSearch" /></label>
        </p>
        <p>
          <label>Payment reference<input
              placeholder="ex: 123-4567890-PMT"
              v-model="paymentRefSearch" /></label>
        </p>
        <p>
          <label>Payee email
            <input
              type="email"
              v-model="payeeEmailSearch"
              :class="{ invalid: !isSearchEmailValid }" /></label>
        </p>
        <p>
          <label>Order ID <input v-model="orderIdSearch" class="order-id-search" /></label>
        </p>
      </fieldset>
      <fieldset class="dates">
        <legend>Creation dates</legend>
        <p>
          <label>From :
            <input
              type="date"
              id="from-date"
              :max="maxDate"
              v-model="fromDate"
              :class="{ invalid: invalidCreationDates }" /></label>
        </p>
        <p>
          <label>To :
            <input
              type="date"
              id="to-date"
              :max="maxDate"
              v-model="toDate"
              :class="{ invalid: invalidCreationDates }" /></label>
        </p>
      </fieldset>
      <fieldset class="operator">
        <legend>Operator</legend>
        <p>
          <label>
            Operator Type
            <select name="operator type" v-model="operatorType">
              <option value="both">Both</option>
              <option value="manual">Merchant</option>
              <option value="automatic">Triple A</option>
            </select>
          </label>
        </p>
        <p>
          <label>Operator name<input
              v-model="operatorSearch"
              :disabled="isOperatorAuto === true" /></label>
        </p>
      </fieldset>
      <fieldset class="dates">
        <legend>Last status update</legend>
        <p>
          <label for="status">Current Status
            <select name="status" v-model="statusSearch">
              <option value=""></option>
              <option
                v-for="status in Object.values(REFUND_STATUS)"
                :key="status.value"
                :value="status.value">
                {{ status.display }}
              </option>
            </select>
          </label>
        </p>
        <p>
          <label>From :
            <input
              type="date"
              id="status-from-date"
              :max="maxDate"
              v-model="statusFromDate"
              :class="{ invalid: invalidStatusDates }" /></label>
        </p>
        <p>
          <label>To :
            <input
              type="date"
              id="status-to-date"
              :max="maxDate"
              v-model="statusToDate"
              :class="{ invalid: invalidStatusDates }" /></label>
        </p>
      </fieldset>
      <div class="button-grp">
        <button @click="findRefunds()" class="btn icon-btn search">Search</button>
        <button type="reset" class="btn icon-btn">Reset form</button>
        <button @click="downloadRefundsCSV()" class="btn icon-btn download">Download as CSV</button>
      </div>
      <Transition name="fade">
        <ul
          class="errors-section"
          v-if="
            errorDownloadRefundsAsCsv ||
            invalidCreationDates ||
            invalidStatusDates ||
            !isSearchEmailValid
          ">
          <li v-if="errorDownloadRefundsAsCsv">
            Error in downloading csv file, please try again.
          </li>
          <li v-if="invalidCreationDates">
            Invalid <strong>Creation dates</strong> :
            <strong>To Date</strong> should not be earlier than
            <strong>From Date</strong>
          </li>
          <li v-if="invalidStatusDates">
            Invalid <strong>Last modified dates</strong> :
            <strong>To Date</strong> should not be earlier than
            <strong>From Date</strong>
          </li>
          <li v-if="!isSearchEmailValid">Please enter a valid email</li>
        </ul>
      </Transition>
    </form>

    <!-- REFUNDS -->
    <div class="table-wrapper-custom">
      <div>
        <table aria-label="Payout list">
          <thead>
            <tr>
              <th class="sticky-col left">Payout reference</th>
              <th class="status">Status</th>
              <th>Payee Email</th>
              <th>Creation date</th>
              <th>Last modified</th>
              <th>Refunded amount</th>
              <th>Payment reference</th>
              <th>Operator</th>
              <th>Refund Reason</th>
              <th>Reason Provided By</th>
              <th class="sticky-col right">Action</th>
            </tr>
          </thead>
          <Transition name="fade">
            <tbody
              v-if="refunds && refunds.length > 0"
              is="TransitionGroup"
              name="fade">
              <tr v-for="refund in refunds" :key="refund.payout_reference">
                <td :data="refund.payout_reference" class="sticky-col left">
                  <button
                    v-if="!isWallet"
                    class="btn link-style"
                    @click="handleSelectedRefund(refund)">
                    {{ refund.payout_reference }}
                  </button>
                  <button
                    v-else
                    class="btn link-style"
                    @click="handleSelectedRefund(refund)">
                    {{ '-' }}
                  </button>
                </td>
                <td :data="refund.status">
                  <tag>{{ REFUND_STATUS[refund.status.toUpperCase()].display }}</tag>
                </td>
                <td :data="refund.payee_email">{{ refund.payee_email }}</td>
                <td :data="refund.payout_date" class="dates">
                  {{
                  refund.payout_date
                  ? `${formatToLocalDate(
                  refund.payout_date
                  )}, ${formatToLocalTime(refund.payout_date)}`
                  : '-'
                  }}
                </td>
                <td :data="refund.status_date" class="dates">
                  {{
                  refund.status_date
                  ? `${formatToLocalDate(
                  refund.status_date
                  )}, ${formatToLocalTime(refund.status_date)}`
                  : '-'
                  }}
                </td>
                <td
                  :data="refund.crypto_amount"
                  v-if="refund.account_type === 'custody'">
                  {{ refund.crypto_currency }}
                  {{ formatCryptoPrice(refund.crypto_amount) }}
                </td>
                <td :data="refund.withdraw_amount" v-else>
                  {{ refund.payout_currency }}
                  {{ formatFiatPrice(refund.withdraw_amount) }}
                </td>
                <td :data="refund.payment_reference_number">
                  {{ refund.payment_reference_number }}
                </td>
                <td>{{ formatOperator(refund.operator, refund.is_auto) }}</td>
                <td>
                  {{
                  refund.refund_reason
                  ? refund.refund_reason.split('_').join(' ')
                  : '-'
                  }}
                </td>
                <td>
                  {{
                  refund.reason_provided_by ? refund.reason_provided_by : '-'
                  }}
                </td>
                <td class="actions sticky-col right">
                  <span v-if="refund.status === 'new'">
                    <button
                      v-if="hasRightsToEmail"
                      class="pure-icon"
                      @click.stop="openResendEmailPopup(refund)">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24"
                        width="24"
                        fill="#FFF">
                        <path d="M0 0h24v24H0V0z" fill="none" />
                        <path
                          d="M22 6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6zm-2 0-8 5-8-5h16zm0 12H4V8l8 5 8-5v10z" />
                      </svg>
                      <span class="tooltip-text">
                        {{
                        hasRightsToChangeEmail
                        ? 'Resend or Change email'
                        : 'Resend email'
                        }}
                      </span>
                    </button>
                    <button
                      v-if="hasRightsToCancel"
                      @click.stop="openConfirmCancelRefundPopup(refund)"
                      class="pure-icon cancel">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        height="24"
                        width="24"
                        fill="#FFF">
                        <path d="M0 0h24v24H0z" fill="none" />
                        <path
                          d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                      </svg>
                      <span class="tooltip-text">Cancel this refund</span>
                    </button>
                  </span>
                  <span v-else>-</span>
                </td>
              </tr>
            </tbody>
          </Transition>
        </table>
      </div>
      <Transition name="fade">
        <p
          class="emptyTable"
          v-if="!refunds || (refunds && refunds.length <= 0)">
          {{
          firstSearchMade
          ? 'No refund matches this search, please try with other parameters'
          : "You don't have any refunds yet"
          }}
        </p>
      </Transition>
      <ul
        style="display: flex; justify-content: center">
        <li>
          <button :disabled="isPrevRefundListExist" @click="prevRefundList" class="btn">Previous</button>
        </li>
        <li>
          <button :disabled="isNextRefundListExist" @click="nextRefundList" class="btn">Next</button>
        </li>
      </ul>
    </div>
    <!-- Status wiki -->
    <article class="status">
      Here is the meaning of all the statuses used in the table. You can find
      out more about them in the
      <a
        href="https://developers.triple-a.io/docs/triplea-api-doc/0d10abc289f23-payout-statuses"
        target="_blank"
        rel="noopener">Official documentation</a>, or ask your question in the
      <a
        href="https://support.triple-a.io/knowledge"
        target="_blank"
        rel="noopener">FAQ</a>.
      <dl>
        <div v-for="status in Object.values(REFUND_STATUS)" :key="status.value">
          <dt>
            <tag>{{ status.display }}</tag>
          </dt>
          <dd>{{ status.definition }}</dd>
        </div>
      </dl>
    </article>

    <triplea-refund-popup
      :shouldDisplay="shouldDisplayDetailsPopup"
      @cancel-clicked="openConfirmCancelRefundPopup(selectedRefundGetter)"
      @closing-refund-details-popup="handleClosingDetailsPopup()"
      @email-clicked="openResendEmailPopup(selectedRefundGetter)" />
    <!--DIALOG / POPUP FOR EMAIL-->
    <vs-prompt
      :active.sync="showResendEmailPopup"
      title="Resend/Change email"
      accept-text="Send Email"
      @accept="confirmResendEmail()"
      @close="closeResendEmailPopup()"
      @cancel="closeResendEmailPopup()"
      :is-valid="!showChangeEmailScreen && selectedRefundEmail"
      class="resend-email-popup">
      <fieldset v-if="showChangeEmailScreen">
        <label>Insert a new email <input v-model="newEmail" /></label>
        <div>
          <button @click="backToDefaultChangeEmailScreen()">Back</button>
          <button :disabled="!isNewEmailValid" @click="updateEmail()">
            Update and send Email
          </button>
        </div>
      </fieldset>
      <div v-else class="resend-email-popup-body">
        <p v-if="showResendEmailPopup">
          Refund email will be re-sent to
          <strong>{{ selectedRefundEmail }}</strong>
        </p>
        <button v-if="hasRightsToChangeEmail" @click="goToChangeEmailScreen()">
          Change Email
        </button>
      </div>
    </vs-prompt>

    <vs-prompt
      :active.sync="showConfirmCancelPopup"
      title="Cancel Refund"
      accept-text="Cancel Refund"
      cancel-text="Close"
      color="danger"
      @accept="cancelRefund(selectedRefundGetter)"
      @close="closeConfirmCancelRefundPopup()"
      @cancel="closeConfirmCancelRefundPopup()">
      You are trying to cancel the refund
      <strong v-if="selectedRefundGetter">{{
        selectedRefundGetter.payout_reference
        }}</strong>
    </vs-prompt>
  </main>
</template>

<script>
import { CRYPTO_REFUND_STATUS } from '../../constants';
import TripleaRefundPopup from '../../components/triplea-tx-popup/TripleaRefundPopup';

import fileDownload from 'js-file-download';

import { mapActions, mapGetters } from 'vuex';
import Tag from '../../atoms/Tag.vue';

export default {
  components: { Tag, TripleaRefundPopup },
  data() {
    return {
      debugConsole: false,
      REFUND_STATUS: CRYPTO_REFUND_STATUS,
      // Wallet info
      apiId: '',
      name: '',
      daysToDisplay: 60,
      notificationEmail: '',
      pubkey: '',

      fromDate: '',
      toDate: '',
      statusFromDate: '',
      statusToDate: '',
      maxDate: new Date(Date.now()).toISOString().split('T')[0],
      statusSearch: '',
      paymentRefSearch: '',
      refundRefSearch: '',
      operatorSearch: '',
      operatorType: 'both',
      isOperatorAuto: '',
      payeeEmailSearch: '',
      orderIdSearch: '',
      invalidCreationDates: false,
      invalidStatusDates: false,
      isSearchEmailValid: true,
      // Flag to differenciate no existing refunds and no results found
      firstSearchMade: false,

      errorDownloadRefundsAsCsv: false,
      errorDownloadPayoutAsCsv: false,
      selectedRefund: null,

      itemsPerPage: 50,
      maxItemsPerPage: {
        type: Number,
        default: null,
      },
      itemsPerPageOptions: [
        {
          label: '5',
          value: 5,
        },
        {
          label: '10',
          value: 10,
        },
        {
          label: '15',
          value: 15,
        },
        {
          label: '20',
          value: 20,
        },
        {
          label: '50',
          value: 50,
        },
      ],

      hasRightsToCancel: false,
      hasRightsToEmail: false,
      hasRightsToChangeEmail: false,
      showResendEmailPopup: false,
      selectedRefundEmail: null,
      newEmail: '',

      showChangeEmailScreen: false,
      shouldDisplayDetailsPopup: false,

      showConfirmCancelPopup: false,

      // constants
      operator_manual_prefix: 'Merchant (',
      operator_manual_suffix: ')',
      operator_auto_value: 'Triple A Technologies',
    };
  },
  computed: {
    ...mapGetters([
      'activeAccountGetter',
      'localCurrencyGetter',
      'activeMerchantGetter',
      'localCurrencyTxnGetter',
      'refundsGetter',
      'refundMetaDataGetter',
      'selectedRefundGetter',
      'associatedPaymentGetter',
      'currentActiveEntityAdminGetter',
    ]),

    refunds() {
      let refunds = this.refundsGetter;
      return refunds;
    },
    isWallet() {
      if (
        this.activeAccountGetter &&
        this.activeAccountGetter.cryptoCurrency.length > 4 &&
        this.activeAccountGetter.cryptoCurrency !== 'testBTC' &&
        !this.activeAccountGetter.cryptoCurrency.includes('_')
      ) {
        return true;
      } else {
        return false;
      }
    },

    isNetwork() {
      if (
        this.activeAccountGetter &&
        this.activeAccountGetter.cryptoCurrency.includes('_')
      ) {
        return true;
      } else {
        return false;
      }
    },

    isNextRefundListExist() {
      if (this.refundMetaDataGetter && this.refundMetaDataGetter.next) {
        return false;
      } else {
        return true;
      }
    },

    isPrevRefundListExist() {
      if (this.refundMetaDataGetter && this.refundMetaDataGetter.prev) {
        return false;
      } else {
        return true;
      }
    },
    isNewEmailValid() {
      // can add further domain checks here
      return this.isEmail(this.newEmail);
    },
  },
  watch: {
    activeAccountGetter(newValue) {
      if (this.debugConsole) console.log(newValue);
      this.pageInit();
    },
    operatorType(newValue) {
      this.handleOperatorType(newValue);
    },
    isOperatorAuto(newValue) {
      if (newValue === true) {
        this.operatorSearch = 'Triple A Technologies';
      } else {
        this.operatorSearch = '';
      }
    },
  },
  mounted() {
    this.pageInit();
  },
  updated() { },
  beforeDestroy() { },

  methods: {
    ...mapActions([
      'fetchAddressesAction',
      'downloadTransactionAsCSV',
      'downloadPayoutAsCSV',
      'fetchMerchantRefundList',
      'fetchNextRefundListAction',
      'fetchPrevRefundListAction',
      'cancelRefundwithRef',
      'resendEmail',
      'changeEmail',
    ]),

    pageInit() {
      this.account = this.activeAccountGetter;
      if (!this.account || !this.account.apiId) {
        console.error('No active wallet found.');
        return;
      }
      this.resetForm();
      let payload = {};
      payload.merchant_key =
        this.activeMerchantGetter && this.activeMerchantGetter.merchant_key;

      this.fetchMerchantRefundList(payload)
        .then((res) => {
          return res;
        })
        .catch((err) => {
          console.log('Error in Getting Refunds List', err);
        });

      this.getRights();
    },

    getRights() {
      const currentActiveMerchantId =
        this.activeMerchantGetter && this.activeMerchantGetter.id;
      this.hasRightsToCancel = this.isAllowed(
        this.currentActiveEntityAdminGetter,
        currentActiveMerchantId,
        'payout_links',
        'D'
      );
      this.hasRightsToEmail = this.isAllowed(
        this.currentActiveEntityAdminGetter,
        currentActiveMerchantId,
        'payout_links',
        'R'
      );
      this.hasRightsToChangeEmail = this.isAllowed(
        this.currentActiveEntityAdminGetter,
        currentActiveMerchantId,
        'payout_links',
        'W'
      );
    },

    isFormValid() {
      let areValuesValid = true;
      // email format
      if (this.payeeEmailSearch && !this.isEmail(this.payeeEmailSearch)) {
        this.isSearchEmailValid = false;
        areValuesValid = false;
      } else {
        this.isSearchEmailValid = true; // resetting error value
      }

      // Creation dates
      if (!this.areDatesValid(this.fromDate, this.toDate)) {
        areValuesValid = false;
        this.invalidCreationDates = true;
      } else {
        this.invalidCreationDates = false;
      }

      // Modified dates
      if (!this.areDatesValid(this.statusFromDate, this.statusToDate)) {
        areValuesValid = false;
        this.invalidStatusDates = true;
      } else {
        this.invalidStatusDates = false;
      }

      return areValuesValid;
    },

    buildPayload() {
      let payload = {};
      payload.merchant_key =
        this.activeMerchantGetter && this.activeMerchantGetter.merchant_key;

      // Add params to payload depending on filled inputs
      if (this.isOperatorAuto === true || this.isOperatorAuto === false) {
        payload.is_auto = this.isOperatorAuto; // default value is non boolean
      }
      if (this.operatorSearch) {
        this.operatorSearch = this.operatorSearch.trim();
        if (this.isOperatorSystem(this.operatorSearch)) {
          payload.is_auto = true;
        } else {
          if (this.operatorSearch.includes(this.operator_manual_prefix)) {
            payload.operator = this.extractOperatorName(this.operatorSearch);
          } else {
            payload.operator = this.operatorSearch;
          }
          payload.is_auto = false;
        }
      }
      if (this.paymentRefSearch) {
        payload.payment_reference = this.paymentRefSearch;
      }
      if (this.refundRefSearch) {
        payload.payout_reference = this.refundRefSearch;
      }
      if (this.orderIdSearch) {
        payload.order_id = this.orderIdSearch;
      }
      if (this.statusSearch) {
        payload.status = this.statusSearch;
      }
      if (this.payeeEmailSearch) {
        payload.email = this.replaceReservedChars(this.payeeEmailSearch);
      }

      if (this.fromDate) payload.from_date = this.formatDate(this.fromDate);
      if (this.toDate) payload.to_date = this.getNextDay(this.toDate);

      if (this.statusFromDate)
        payload.from_modified_date = this.formatDate(this.statusFromDate);
      if (this.statusToDate)
        payload.to_modified_date = this.getNextDay(this.statusToDate);

      return payload;
    },

    findRefunds() {
      if (!this.isFormValid()) {
        return;
      }
      const payload = this.buildPayload();
      if (this.debugConsole) console.log('search params :', payload);

      this.fetchMerchantRefundList(payload)
        .then((res) => {
          if (this.debugConsole) console.log('Result of Refunds', res);
          this.firstSearchMade = true;
        })
        .catch((err) => {
          console.log('Error in Getting Refunds List', err);
        });
      return false;
    },

    handleSelectedRefund(refund) {
      if (this.debugConsole) console.log('This selectedRefund', refund);
      this.selectedRefund = refund;
      this.shouldDisplayDetailsPopup = true;
      this.$store.commit('UPDATE_SELECTED_REFUND', this.selectedRefund);
      this.$store.dispatch(
        'fetchPaymentInfos',
        this.selectedRefundGetter.payment_reference_number
      );
    },

    cancelRefund(item) {
      if (item && item.payout_reference) {
        if (this.debugConsole) console.log('Trying to cancel the refund', item);
        // getting api token
        const activeMerchantKey = this.activeMerchantGetter.merchant_key;
        let payload = {
          ref: item.payout_reference,
          merchant_key: activeMerchantKey,
        };

        this.$recaptchaLoaded()
          .then(() => {
            this.$recaptcha('browser_access').then((token) => {
              payload.recaptchaToken = token;

              this.$store
                .dispatch('cancelRefundWithRef', payload)
                .then((res) => {
                  if (this.debugConsole) {
                    console.log('successfully cancelled the refund ', res);
                  }

                  const updatedRefund = { ...item, ...res.data }; // quickwin to have the popup updated
                  this.$store.commit('UPDATE_SELECTED_REFUND', updatedRefund);
                  // pull the list again to have the updates
                  this.statusSearch = ''; // resetting the status search to include the newly modified refund
                  this.findRefunds();
                  // get Merchants to update the balance
                  this.apiGetMerchants().then(res => {
                    this.$store.commit('UPDATE_ACTIVE_MERCHANT', res.data.find(merchant => merchant.merchant_key === activeMerchantKey));
                  });
                })
                .catch((err) => {
                  throw err;
                });
            });
          }).catch(() => {
            this.$vs.notify({
              title: 'Recaptcha Error',
              text: 'Error in cancelling refund. Please refresh or try re-login again.',
              color: 'danger',
            });

          });
      } else {
        console.error('missing payout reference');
      }
    },

    async nextRefundList() {
      if (this.refundMetaDataGetter.next) {
        try {
          let payload = {};
          payload.merchant_key =
            this.activeMerchantGetter && this.activeMerchantGetter.merchant_key;
          let result = await this.fetchNextRefundListAction(payload);
          if (this.debugConsole)
            console.log('Fetch Next PayoutList successful', result);
        } catch (err) {
          console.log('Fetch Next PayoutList error', err);
        }
      }
    },

    async prevRefundList() {
      if (this.refundMetaDataGetter.prev) {
        try {
          let payload = {};
          payload.merchant_key =
            this.activeMerchantGetter && this.activeMerchantGetter.merchant_key;
          let result = await this.fetchPrevRefundListAction(payload);
          if (this.debugConsole)
            console.log('Fetch Prev PayoutList successful', result);
        } catch (err) {
          console.log('Fetch Prev PayoutList error', err);
        }
      }
    },

    downloadRefundsCSV() {
      if (!this.isFormValid()) {
        return;
      }
      const payload = this.buildPayload();
      payload.type = 'refund';

      this.downloadPayoutAsCSV(payload)
        .then((res) => {
          if (this.debugConsole)
            console.log('Result of download as payout csv', res);
          fileDownload(res.data, 'payout.csv');
          this.errorDownloadPayoutAsCsv = false;
        })
        .catch((err) => {
          if (this.debugConsole)
            console.log('Error in download transaction as csv', err);
          this.errorDownloadPayoutAsCsv = true;
        });
    },

    areDatesValid(fromD, toD) {
      if (!fromD || !toD) return true; // only one set
      if (fromD && toD && new Date(fromD) <= new Date(toD)) {
        return true;
      } else {
        return false;
      }
    },

    resetDates() {
      this.fromDate = '';
      this.toDate = '';
    },

    isEmail(str) {
      const emailRegex = /^\w+([.+-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
      return emailRegex.test(str);
    },

    resetForm() {
      this.fromDate = '';
      this.toDate = '';
      this.statusFromDate = '';
      this.statusToDate = '';
      this.statusSearch = '';
      this.paymentRefSearch = '';
      this.refundRefSearch = '';
      this.operatorSearch = '';
      this.payeeEmailSearch = '';
      this.operatorType = 'both';
      this.orderIdSearch = '';
      this.isOperatorAuto = null;
    },

    openResendEmailPopup(tr) {
      this.selectedRefund = tr;
      this.selectedRefundEmail = tr.payee_email;
      if (this.debugConsole)
        console.log('Selected Refund Email', this.selectedRefundEmail);
      this.showResendEmailPopup = true;
    },
    closeResendEmailPopup() {
      this.showResendEmailPopup = false;
      this.showChangeEmailScreen = false;
    },

    goToChangeEmailScreen() {
      this.showChangeEmailScreen = true;
    },

    updateEmail() {
      if (this.debugConsole) console.log('New Email', this.newEmail);
      const payload = {
        merchant_key: this.activeMerchantGetter.merchant_key,
        payout_reference: this.selectedRefund.payout_reference,
        newEmail: this.newEmail,
      };
      this.$store
        .dispatch('updateEmail', payload)
        .then((res) => {
          this.selectedRefundEmail = res.data.email;
          const updatedRefund = { ...this.selectedRefundGetter, ...res.data }; // quickwin to have the popup updated
          updatedRefund.payee_email = res.data.email;
          this.$store.commit('UPDATE_SELECTED_REFUND', updatedRefund);
          this.payeeEmailSearch = ''; // because if we update the email while this filter is on, user might not understand why the refund disappeared
          this.findRefunds();
          this.closeResendEmailPopup();
        })
        .catch((err) => {
          throw err;
        });
    },
    backToDefaultChangeEmailScreen() {
      this.newEmail = '';
      this.showChangeEmailScreen = false;
    },
    confirmResendEmail() {
      if (this.debugConsole) console.log('Resend Email', this.selectedRefund);
      const activeMerchantKey = this.activeMerchantGetter.merchant_key;
      const payload = {};
      payload.merchant_key = activeMerchantKey;
      payload.payout_reference = this.selectedRefund.payout_reference;
      this.$store.dispatch('resendEmail', payload);
      this.showResendEmailPopup = false;
      this.selectedRefund = null;
    },
    handleClosingDetailsPopup() {
      this.shouldDisplayDetailsPopup = false;
      this.selectedRefund = null;
      this.closeConfirmCancelRefundPopup();
    },

    handleReset() {
      this.resetForm();
      this.findRefunds();
    },

    closeConfirmCancelRefundPopup() {
      this.showConfirmCancelPopup = false;
    },
    openConfirmCancelRefundPopup(refund) {
      this.showConfirmCancelPopup = true;
      this.$store.commit('UPDATE_SELECTED_REFUND', refund);
    },
    formatOperator(op, is_auto) {
      if (is_auto) {
        // const tier = op.split('_')[1];
        return this.operator_auto_value;
      } else {
        return this.operator_manual_prefix + op + this.operator_manual_suffix;
      }
    },
    extractOperatorName(op) {
      op = op.trim();
      if (this.isOperatorSystem(op)) {
        return this.formatOperator('', true);
      }
      const offset_start = this.operator_manual_prefix.length;
      const offset_end = this.operator_manual_suffix.length;
      return op.substring(offset_start, op.length - offset_end); // removing the extra text
    },
    handleOperatorType(val) {
      if (val === 'automatic') {
        this.isOperatorAuto = true;
      } else if (val === 'manual') {
        this.isOperatorAuto = false;
      } else {
        this.isOperatorAuto = 'not specified';
      }
    },
    isOperatorSystem(val) {
      let op = val.trim();
      return (
        op.toLowerCase() === 'system' ||
        op.toLowerCase() === this.operator_auto_value.toLowerCase()
      );
    },

    statusClass(status) {
      return status;
    },
  },
};
</script>

<style scoped lang="scss">
main {
  &>h1 {
    text-transform: uppercase;
    font-size: 32px;
    font-weight: bold;
    color: #505050;
    margin-bottom: 24px;
  }

  form {
    margin-bottom: $margin-m;
    display: flex;
    flex-wrap: wrap;

    fieldset {
      border: none;
      display: flex;
      flex-wrap: wrap;
      margin-bottom: 12px;
      margin-right: 32px;
      align-items: flex-end;

      p {
        padding-right: 6px;

        &:last-of-type {
          padding-right: 0;
        }
      }

      label {
        font-size: 12px;
      }

      input,
      select {
        display: block;
        width: 100%;
        max-width: 210px;
        height: 38px;
        border-radius: 5px;
        padding: 3px;
        border: 0.5px solid lightgrey;

        &[type='date'] {
          min-width: 150px;
        }

        &[type='email'] {
          min-width: 300px;
        }

        &.order-id-search {
          min-width: 300px;
        }

        &.invalid {
          border-color: red;
        }
      }

      select {
        text-transform: capitalize;
      }

      &.dates {
        display: flex;
        align-items: flex-end;

        button {
          background-color: rgb(7, 85, 147);
          color: white;
          padding: 12px;
          margin-left: 8px;
          margin-top: 8px;
          border-radius: 6px;
          border: none;
          text-align: center;
          font-family: 'Montserrat', Helvetica, Arial, sans-serif;
          font-size: 1rem;
          max-height: 38px;

          &:hover {
            cursor: pointer;
            background-color: rgb(5, 59, 102);
          }
        }
      }

      &.operator {
        p {
          label input:disabled {
            color: #b5b5b5;
          }
        }
      }

      &.references {
        p {
          label {
            span {
              font-weight: bold;
            }
          }
        }
      }
    }

    .button-grp {
      display: flex;
      margin-top: $margin-m;
      margin-bottom: $margin-m;
      width: 100%;

      button:last-child {
        margin-right: 0;
        margin-left: auto;
      }
    }

    ul.errors-section {
      color: red;
      font-size: 12px;
    }
  }

  .table-wrapper-custom {
    table {
      td {
        button.btn.link-style {
          border: none;
          background-color: transparent;
          color: #505050;

          &:hover {
            color: rgb(7, 85, 147);
            text-decoration: underline;
          }
        }
      }
    }
  }

  article {
    margin-top: 24px;

    dl {
      margin-top: 12px;

      div {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        margin-bottom: 12px;

        dt {
          margin-right: 6px;
        }
      }
    }
  }
}

.resend-email-popup {
  .resend-email-popup-body {
    display: flex;
    flex-flow: column;
    justify-content: center;

    p {
      text-align: center;
    }

    button {
      margin: auto;
      margin-top: 8px;
    }
  }

  fieldset {
    border: none;

    label {
      input {
        width: 100%;
        font-size: 1rem;
        padding: 0.7rem;
        border: 1px solid rgba(0, 0, 0, 0.2);
        border-radius: 6px;
      }
    }

    div {
      display: flex;
      margin-top: 12px;

      button {
        margin-right: 8px;

        &:last-of-type {
          margin-right: 0;
        }
      }
    }
  }

  button {
    padding: 0.75rem 2rem;
    background: rgb(7, 85, 147);
    color: white;
    font-size: 1rem;
    border: none;
    border-radius: 6px;
    font-family: 'Montserrat', Helvetica, Arial, sans-serif;
    transition: ease-in-out 0.5;

    &:disabled {
      opacity: 0.5;
      cursor: default;
      pointer-events: none;
    }

    &:hover {
      box-shadow: 0px 6px 12px 0px rgba(7, 85, 147, 0.5);
    }
  }
}
</style>
